import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/App";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, Plus, Search, ChevronUp, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SECTIONS = ["General"];

/** Highlight matching text within a string, returning React elements.
 *  split() with a capturing group places matches at odd indices (1, 3, 5…). */
const HighlightedText = ({ text, term, isCurrent }) => {
  if (!term) return <>{text}</>;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className={`bg-yellow-300/80 text-inherit rounded-sm px-0.5 ${isCurrent ? 'ring-2 ring-yellow-500' : ''}`}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export const ChatPanel = ({ transactionId }) => {
  const { user, profile, displayName } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewSection, setShowNewSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0);
  const messageRefs = useRef({});
  const searchInputRef = useRef(null);

  const activeThread = threads.find(t => t.id === activeThreadId) || null;

  // Compute search matches: indices of messages (in flat order) that contain the term
  const matchedMessageIds = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    // Flatten grouped messages into chronological order
    return messages
      .filter(m => m.content?.toLowerCase().includes(term))
      .map(m => m.id);
  }, [messages, searchTerm]);

  // Reset match index when matches change
  useEffect(() => {
    if (matchedMessageIds.length > 0) {
      setCurrentMatchIdx(0);
    }
  }, [matchedMessageIds.length, searchTerm]);

  // Scroll current match into view
  useEffect(() => {
    if (matchedMessageIds.length > 0 && searchTerm) {
      const currentId = matchedMessageIds[currentMatchIdx];
      const el = messageRefs.current[currentId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentMatchIdx, matchedMessageIds, searchTerm]);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchTerm("");
    setCurrentMatchIdx(0);
  };

  const goNextMatch = () => {
    if (matchedMessageIds.length === 0) return;
    setCurrentMatchIdx(prev => (prev + 1) % matchedMessageIds.length);
  };

  const goPrevMatch = () => {
    if (matchedMessageIds.length === 0) return;
    setCurrentMatchIdx(prev => (prev - 1 + matchedMessageIds.length) % matchedMessageIds.length);
  };

  // Step 1: Fetch or create all section threads on mount
  useEffect(() => {
    fetchAllThreads();
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [transactionId]);

  // Step 2: When active thread changes, fetch messages + subscribe to Realtime
  useEffect(() => {
    if (!activeThreadId) return;

    fetchMessages(activeThreadId);
    closeSearch();

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channelName = `chat-realtime:${activeThreadId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `thread_id=eq.${activeThreadId}`
        },
        async (payload) => {
          const alreadyExists = await new Promise(resolve => {
            setMessages(prev => {
              resolve(prev.some(m => m.id === payload.new.id));
              return prev;
            });
          });

          if (alreadyExists) return;

          const { data, error } = await supabase
            .from('chat_messages')
            .select(`*, sender:sender_id (full_name, email)`)
            .eq('id', payload.new.id)
            .single();

          if (error || !data) return;

          setMessages(prev => {
            if (prev.some(m => m.id === data.id)) return prev;
            return [...prev, data];
          });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [activeThreadId]);

  // Auto-scroll on new messages (only when search is not active)
  useEffect(() => {
    if (!searchTerm) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, searchTerm]);

  const fetchAllThreads = async () => {
    try {
      const { data: existing, error } = await supabase
        .from('chat_threads')
        .select('*')
        .eq('transaction_id', transactionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const existingNames = new Set((existing || []).map(t => t.name));
      const missing = DEFAULT_SECTIONS.filter(name => !existingNames.has(name));

      let allThreads = existing || [];

      if (missing.length > 0) {
        const { data: created, error: createError } = await supabase
          .from('chat_threads')
          .insert(missing.map(name => ({ transaction_id: transactionId, name })))
          .select();

        if (createError) {
          console.warn('[Chat] Error creating threads, re-fetching:', createError.message);
          const { data: refreshed } = await supabase
            .from('chat_threads')
            .select('*')
            .eq('transaction_id', transactionId)
            .order('created_at', { ascending: true });
          allThreads = refreshed || allThreads;
        } else {
          allThreads = [...allThreads, ...(created || [])];
        }
      }

      const defaultOrder = new Map(DEFAULT_SECTIONS.map((name, i) => [name, i]));
      allThreads.sort((a, b) => {
        const aIdx = defaultOrder.has(a.name) ? defaultOrder.get(a.name) : 100;
        const bIdx = defaultOrder.has(b.name) ? defaultOrder.get(b.name) : 100;
        if (aIdx !== bIdx) return aIdx - bIdx;
        return a.name.localeCompare(b.name);
      });

      setThreads(allThreads);

      const general = allThreads.find(t => t.name === "General");
      setActiveThreadId(general?.id || allThreads[0]?.id || null);
    } catch (error) {
      console.error('[Chat] Error fetching threads:', error);
      toast.error("Failed to load chat");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`*, sender:sender_id (full_name, email)`)
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('[Chat] Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeThreadId) return;

    const messageContent = newMessage;
    setNewMessage("");
    setSending(true);

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          thread_id: activeThreadId,
          sender_id: user.id,
          content: messageContent
        })
        .select()
        .single();

      if (error) throw error;

      const localMessage = {
        ...data,
        sender: {
          full_name: displayName || 'You',
          email: profile?.email || user?.email
        }
      };
      setMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, localMessage];
      });
    } catch (error) {
      console.error('[Chat] Error sending message:', error);
      toast.error("Failed to send message");
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleCreateSection = async () => {
    const name = newSectionName.trim();
    if (!name) return;

    if (threads.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      toast.error("A section with that name already exists");
      return;
    }

    try {
      const { data, error } = await supabase
        .from('chat_threads')
        .insert({ transaction_id: transactionId, name })
        .select()
        .single();

      if (error) throw error;

      setThreads(prev => [...prev, data]);
      setActiveThreadId(data.id);
      setNewSectionName("");
      setShowNewSection(false);
      toast.success(`"${name}" section created`);
    } catch (error) {
      console.error('[Chat] Error creating section:', error);
      toast.error("Failed to create section");
    }
  };

  const switchSection = (threadId) => {
    if (threadId === activeThreadId) return;
    setMessages([]);
    setActiveThreadId(threadId);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  const currentMatchId = matchedMessageIds[currentMatchIdx] || null;

  return (
    <Card className="border-border/60 shadow-soft" data-testid="chat-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Team Chat
          </CardTitle>
          <Button
            variant="ghost"
            className="h-8 px-2 gap-1.5"
            onClick={searchOpen ? closeSearch : openSearch}
            title="Search messages"
            data-testid="chat-search-toggle"
          >
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium text-muted-foreground">Search</span>
          </Button>
        </div>
        <CardDescription className="text-sm">
          Communicate with all transaction participants
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Section selector */}
        <div className="mb-3" data-testid="chat-section-selector">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {threads.map(t => (
              <button
                key={t.id}
                onClick={() => switchSection(t.id)}
                className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  t.id === activeThreadId
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                data-testid={`chat-section-${t.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {t.name}
              </button>
            ))}
            {!showNewSection && (
              <button
                onClick={() => setShowNewSection(true)}
                className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                title="Add section"
                data-testid="add-chat-section-btn"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* New section input */}
          {showNewSection && (
            <div className="flex items-center gap-2 mt-2" data-testid="new-section-form">
              <Input
                placeholder="Section name..."
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateSection(); if (e.key === 'Escape') setShowNewSection(false); }}
                className="h-8 text-sm flex-1"
                autoFocus
                data-testid="new-section-input"
              />
              <Button size="sm" className="h-8 text-xs rounded-md" onClick={handleCreateSection} disabled={!newSectionName.trim()} data-testid="create-section-btn">
                Add
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setShowNewSection(false); setNewSectionName(""); }}>
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* Chat area */}
        <div className="border border-border rounded-lg flex flex-col h-[460px]">
          {/* Search bar */}
          {searchOpen && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30" data-testid="chat-search-bar">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <Input
                ref={searchInputRef}
                placeholder="Find in chat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.shiftKey ? goPrevMatch() : goNextMatch(); }
                  if (e.key === 'Escape') closeSearch();
                }}
                className="h-7 text-sm flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
                data-testid="chat-search-input"
              />
              {searchTerm && (
                <span className="text-xs text-muted-foreground tabular-nums shrink-0" data-testid="chat-search-count">
                  {matchedMessageIds.length > 0
                    ? `${currentMatchIdx + 1} of ${matchedMessageIds.length}`
                    : "No results"}
                </span>
              )}
              <div className="flex items-center shrink-0">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={goPrevMatch} disabled={matchedMessageIds.length === 0} data-testid="search-prev">
                  <ChevronUp className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={goNextMatch} disabled={matchedMessageIds.length === 0} data-testid="search-next">
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={closeSearch} data-testid="search-close">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs text-muted-foreground px-2">{date}</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>
                  <div className="space-y-3">
                    {msgs.map((message) => {
                      const isMatch = searchTerm && matchedMessageIds.includes(message.id);
                      const isCurrentMatch = message.id === currentMatchId;
                      return (
                        <div
                          key={message.id}
                          ref={el => { messageRefs.current[message.id] = el; }}
                          className={`flex gap-3 transition-colors ${
                            message.sender_id === user.id ? "flex-row-reverse" : ""
                          } ${isCurrentMatch ? "bg-yellow-50 -mx-2 px-2 py-1 rounded-lg" : ""}`}
                          data-testid={`message-${message.id}`}
                        >
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {message.sender?.full_name?.charAt(0)?.toUpperCase() || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`max-w-[70%] ${message.sender_id === user.id ? "text-right" : ""}`}>
                            <div className={`flex items-center gap-2 mb-1 ${message.sender_id === user.id ? "justify-end" : ""}`}>
                              <span className="text-sm font-medium text-foreground">
                                {message.sender_id === user.id ? "You" : message.sender?.full_name || 'Unknown'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatTime(message.created_at)}
                              </span>
                            </div>
                            <div
                              className={`rounded-2xl px-4 py-2 ${
                                message.sender_id === user.id
                                  ? "bg-primary text-primary-foreground rounded-br-md"
                                  : "bg-muted text-foreground rounded-bl-md"
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">
                                {searchTerm && isMatch ? (
                                  <HighlightedText text={message.content} term={searchTerm} isCurrent={isCurrentMatch} />
                                ) : (
                                  message.content
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {messages.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No messages in {activeThread?.name || "this section"} yet</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <form onSubmit={handleSendMessage} className="p-3 border-t border-border">
            <div className="flex gap-2">
              <Input
                placeholder={`Message ${activeThread?.name || ""}...`}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="flex-1"
                disabled={sending}
                data-testid="message-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={sending || !newMessage.trim()}
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};
